import React, { useState, useMemo, useCallback } from 'react';
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { PEOManagementHeader } from '@/components/admin/peo/header/PEOManagementHeader';
import { PEOProvidersList } from '@/components/admin/peo/PEOProvidersList';
import { PEOProviderDialog } from '@/components/admin/peo/PEOProviderDialog';
import { OrganizationManagementTabs } from '../peo-organizations/OrganizationManagementTabs';
import { usePEOProviders } from '@/components/admin/peo/hooks/usePEOProviders';
import { useProviderFilters } from '@/components/admin/peo/hooks/useProviderFilters';
import DuplicateProvidersAlert from '@/components/admin/peo/DuplicateProvidersAlert';
import { findPotentialDuplicates } from '@/utils/peoProviderUtils';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import type { PEOProvider } from '@/types/peo.types';

export const EnhancedPEOManagement = () => {
  // Provider list state
  const [selectedProvider, setSelectedProvider] = useState<PEOProvider | null>(null);
  const [isProviderDialogOpen, setIsProviderDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Provider management state
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('provider-details');

  // Data fetching
  const { 
    providers, 
    isLoading, 
    isError, 
    error, 
    refetch, 
    isDeletingProvider 
  } = usePEOProviders();

  const { 
    activeFilters, 
    setActiveFilters, 
    hasActiveFilters 
  } = useProviderFilters(providers || [], () => {}, refetch);

  // Filtering logic
  const filteredProviders = useMemo(() => {
    if (!providers) return [];
    return providers.filter(provider =>
      provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.specialties?.some(specialty => 
        specialty.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      provider.states_served?.some(state => 
        state.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [providers, searchTerm]);

  // Duplicate detection
  const duplicates = useMemo(() => {
    if (!providers) return [];
    return findPotentialDuplicates(providers);
  }, [providers]);
  const hasDuplicates = duplicates.length > 0;

  // Event handlers
  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const onToggleDebugInfo = () => setShowDebugInfo(!showDebugInfo);

  const onAddProvider = () => {
    setSelectedProvider(null);
    setIsProviderDialogOpen(true);
  };

  const onEditProvider = (provider: PEOProvider) => {
    setSelectedProvider(provider);
    setIsProviderDialogOpen(true);
  };

  const onManageOrganization = (provider: PEOProvider) => {
    setSelectedProviderId(provider.id);
    setActiveTab('provider-details'); // Start with provider details tab
  };

  const handleBackToProviders = () => {
    setSelectedProviderId(null);
    setActiveTab('provider-details');
  };

  const handleProviderSuccess = () => {
    setIsProviderDialogOpen(false);
    setSelectedProvider(null);
    refetch();
  };

  // If a provider is selected for management, show the management interface
  if (selectedProviderId) {
    const currentProvider = providers?.find(p => p.id === selectedProviderId);
    
    return (
      <AdminLayout>
        <div className="space-y-6">
          {/* Breadcrumb header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToProviders}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Providers</span>
              </Button>
              <div>
                <h1 className="text-2xl font-bold">
                  {currentProvider?.name || 'Provider Management'}
                </h1>
                <p className="text-muted-foreground">
                  Manage provider details and organizational structure
                </p>
              </div>
            </div>
          </div>

          {/* Organization management with provider details integrated */}
          <OrganizationManagementTabs
            providerId={selectedProviderId}
            provider={currentProvider}
            onProviderUpdate={refetch}
          />
        </div>
      </AdminLayout>
    );
  }

  // Default provider list view
  return (
    <AdminLayout>
      {hasDuplicates && (
        <DuplicateProvidersAlert />
      )}
      
      <PEOManagementHeader 
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        providers={providers}
        onToggleDebugInfo={onToggleDebugInfo}
        refetch={refetch}
        onAddProvider={onAddProvider}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
      />
      
      <PEOProvidersList 
        providers={filteredProviders}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onAddProvider={onAddProvider}
        onEditProvider={onEditProvider}
        onManageOrganization={onManageOrganization}
        isDeletingProvider={isDeletingProvider}
        showDebugInfo={showDebugInfo}
      />

      <PEOProviderDialog
        isOpen={isProviderDialogOpen}
        onOpenChange={setIsProviderDialogOpen}
        selectedProvider={selectedProvider}
        onSuccess={handleProviderSuccess}
      />
    </AdminLayout>
  );
};